// Selection / display rules for the member table based on the member's own
// Users.plan vs the team plan. AHA-41755 bulk-action UX.
//
// Background — three states a member row can be in:
//   1. unlicensed + plan='free'           → assignable cohort, dropdown shows "grant"
//   2. licensed (license != null) + plan==teamPlan → revocable cohort
//   3. has personal plan (plan !== 'free' && plan !== teamPlan) → opaque to bulk
//      actions: shows their personal plan in the License column instead of the
//      team's, no dropdown, checkbox disabled. The owner can't grant them a
//      team licence (they're already on their own paid plan) and revocation
//      wouldn't make sense either — what they pay for is independent of the
//      team's seats.

import type { TeamMember } from "./types";

/**
 * `true` when the member's own `Users.plan` is a paid plan that's different
 * from the team's plan (= they have a personal Stripe subscription). Those
 * rows are excluded from bulk selection and the License-column dropdown.
 *
 * Same ambiguity the V2 owner gate has: a member whose personal sub happens
 * to match the team plan exactly is indistinguishable from a team-licensed
 * member by this signal alone. We accept that — both render the same plan
 * tag and the cohort treats them as team-licensed (revocable).
 */
export function hasPersonalPlan(
  member: TeamMember,
  teamPlan: string | null | undefined,
): boolean {
  const plan = member.plan;
  if (!plan || plan === "free") return false;
  if (!teamPlan) return true; // no team plan but member has a paid plan → personal
  return plan !== teamPlan;
}

/** `true` when an unlicensed, non-personal-plan member is in the assignable
 *  cohort — the row a bulk "Assign" would touch. */
export function isAssignableMember(
  member: TeamMember,
  teamPlan: string | null | undefined,
): boolean {
  return !member.license && !hasPersonalPlan(member, teamPlan);
}

/** `true` when a licensed, non-personal-plan member is in the revocable
 *  cohort — the row a bulk "Revoke" would touch. */
export function isRevocableMember(
  member: TeamMember,
  teamPlan: string | null | undefined,
): boolean {
  return Boolean(member.license) && !hasPersonalPlan(member, teamPlan);
}

/**
 * AHA-43057 — `true` when the viewer's role permits granting/revoking THIS
 * member's licence. The OWNER may act on everyone; a team ADMIN may only act
 * on plain members (editor/viewer) — never the owner or another admin. This is
 * a UI-only restriction: the backend licence endpoints keep their existing
 * `requireTeamAdmin` acting-user gate and do NOT check the target's role, so
 * this rule only hides/disables the per-row control and bulk selection. It is a
 * product preference ("admin managing another admin's licence is valid, we just
 * don't want it for now"), not a hard security boundary — hence no server guard.
 *
 * Kept separate from the plan-based assignable/revocable cohorts above: those
 * answer "does the licence model apply to this row", this answers "is the
 * viewer allowed to touch it". Both must hold for a row to be actionable.
 *
 * Closed by construction: only the OWNER (any target) and an ADMIN acting on a
 * plain member (editor/viewer) may act. Every other viewer role — editor,
 * viewer, or an undefined/unknown role — returns false.
 */
export function isRoleActionableByViewer(
  member: TeamMember,
  viewerRole: string | null | undefined,
): boolean {
  if (viewerRole === "owner") return true;
  if (viewerRole === "admin") {
    return member.accountRole !== "owner" && member.accountRole !== "admin";
  }
  return false;
}
